// Bausteine der mobilen Action-Bars (<640) — historisch aus der Kalender-
// Bottom-Bar extrahiert; seit der W4-Konvergenz nutzen alle Routen (inkl.
// Kalender) sie ausschließlich über die Shell-Outlets:
//   - BarSlot: ein Tab-Bar-Slot (Icon oben, Label unten, flex-1)
//   - BarAccentPlus: die ＋-Primäraktion als Accent-Kreis
//   - SheetRow: eine Options-Zeile in SidePanel-Sheets (Label + Häkchen)
//   - MOBILE_BAR_HEIGHT: Bar-Inhaltshöhe in px (ohne Safe-Area-Inset)

import { Check, Plus } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '../../lib/utils'

/** Bar-Inhaltshöhe in px (ohne Safe-Area-Inset) — entspricht `h-14` der
 *  Bar-Zeile (MobileShellBottom). Die CSS-Regel `.ai-fab-above-mobile-bar`
 *  (index.css) hängt an denselben 56px (3.5rem) — bei Änderung dort
 *  mitziehen. */
export const MOBILE_BAR_HEIGHT = 56

/** Ein Tab-Bar-Slot: Icon oben, text-2xs-Label darunter, gleichverteilt
 *  (flex-1). Höhe trägt die h-14-Zeile (56px > 44px-Tap-Minimum); Breite
 *  ist selbst bei 5 Slots auf 320px noch 64px. */
export function BarSlot({
  label,
  icon,
  onClick,
  ariaLabel,
  active = false,
  ariaPressed,
  ariaHasPopup,
  ariaExpanded,
}: {
  label: string
  icon: ReactNode
  onClick: () => void
  ariaLabel: string
  active?: boolean
  ariaPressed?: boolean
  ariaHasPopup?: boolean
  /** aria-expanded für Slots, die ein Panel togglen (z.B. ☰ → Drawer). */
  ariaExpanded?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      aria-haspopup={ariaHasPopup ? 'dialog' : undefined}
      aria-expanded={ariaExpanded}
      className={cn(
        'flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 transition-colors touch:opacity-100',
        active ? 'text-accent' : 'text-text-secondary hover:bg-bg-overlay hover:text-text-primary',
      )}
    >
      <span className="flex h-5 items-center">{icon}</span>
      <span className="max-w-full truncate text-2xs font-medium leading-none">{label}</span>
    </button>
  )
}

/** ＋-Primäraktion: sticht als Accent-Kreis aus der Bar heraus. Disabled
 *  wird deaktiviert statt versteckt gerendert — die Bar bleibt so
 *  layout-stabil (Muster: Kalender-„Erfassen" im View-As-Lesemodus). */
export function BarAccentPlus({
  ariaLabel,
  onClick,
  disabled = false,
  title,
}: {
  ariaLabel: string
  onClick: () => void
  disabled?: boolean
  /** Tooltip; Default = ariaLabel (z.B. Begründung im disabled-Zustand). */
  title?: string
}) {
  return (
    <div className="flex min-w-0 flex-1 items-center justify-center">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        title={title ?? ariaLabel}
        className="flex h-11 w-11 items-center justify-center rounded-full bg-accent text-bg-base shadow-lg transition-transform active:scale-95 touch:opacity-100 disabled:opacity-40"
      >
        <Plus className="h-6 w-6" />
      </button>
    </div>
  )
}

/** Eine Options-Zeile in Bottom-Sheets (SidePanel): Label + Häkchen bei
 *  aktiver Auswahl, 48px Tap-Höhe. */
export function SheetRow({
  active,
  label,
  onClick,
}: {
  active: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-12 w-full items-center gap-2.5 rounded-lg px-3 text-sm transition-colors',
        active ? 'bg-accent/15 text-accent' : 'text-text-primary hover:bg-bg-overlay',
      )}
    >
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      {active && <Check className="h-4 w-4 shrink-0" />}
    </button>
  )
}
